import { AppApiURL } from '@app/app.settings';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  AcceptApplicationDto,
  AddIndividualCustomerProfileDto,
  AddIndividualCustomerProfileResult,
  AddVenueProfileDto,
  AddVenueProfileResult,
  BookingCustomerNotificationsSettingsVm,
  BookingPerformerNotificationsSettingsVm,
  BookingPerformerHiringStatus,
  CreateBookingCustomerDto,
  CreateVenueDto,
  IndividualBookingCustomerDetailsVm,
  InnInfoResult,
  PerformerApplicationVm,
  PerformerApplicationsQueryParams,
  PerformerBookingValuesVm,
  PerformerHiringVm,
  SendApplicationDto,
  UpdatePerformerAvailabilityDto,
  normalizePerformerHiringVm,
  SendHiringDto,
  UpdateBookingCustomerNotificationSettingsDto,
  UpdateBookingPerformerNotificationsSettingsDto,
  UpdateIndividualCustomerProfileDto,
  UpdateVenueAddressDto,
  UpdateVenueProfileDto,
  VenueApplicationsQueryParams,
  VenueDetailsVm,
  VenueListItemVm,
  VenueListQueryParams,
  BookingPerformerListItemVm,
  BookingPerformersListQueryParams,
  PhoneNumberDto
} from './booking.model';
import { PerformerRateDto, UpdatePhotosDto, PerformerType } from '@app/modules/account-modules/performers.model';
import { SocialLinkDto, SocialPlatform } from '@app/modules/account-modules/performers.model';

@Injectable({ providedIn: 'root' })
export class BookingService {
  constructor(private http: HttpClient) {}

  getIndividualBookingCustomerProfile(bookingCustomerId: string): Observable<IndividualBookingCustomerDetailsVm> {
    const id = encodeURIComponent(bookingCustomerId);
    return this.http.get<IndividualBookingCustomerDetailsVm>(
      `${AppApiURL}/booking/customers/individuals/${id}/profile`,
      { withCredentials: true }
    );
  }

  updateIndividualBookingCustomerProfile(
    bookingCustomerId: string,
    payload: UpdateIndividualCustomerProfileDto
  ): Observable<void> {
    const id = encodeURIComponent(bookingCustomerId);
    const body = this.mapUpdateProfilePayload(payload);
    return this.http
      .put(`${AppApiURL}/booking/customers/individuals/${id}/profile`, body, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  updateBookingCustomerMainPhoto(bookingCustomerId: string, file: File): Observable<void> {
    const id = encodeURIComponent(bookingCustomerId);
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http
      .post<unknown>(`${AppApiURL}/booking/customers/${id}/photo/update`, formData, {
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  uploadBookingCustomerPhoto(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http
      .post(`${AppApiURL}/booking/customers/photo`, formData, {
        withCredentials: true,
        observe: 'response',
        responseType: 'text'
      })
      .pipe(
        map(res => this.parsePhotoBufferKey(res.body)),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  addIndividualCustomerProfile(
    payload: AddIndividualCustomerProfileDto
  ): Observable<AddIndividualCustomerProfileResult> {
    const body = this.mapAddIndividualProfilePayload(payload);
    return this.http
      .post(`${AppApiURL}/booking/customers/individuals/profile`, body, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map(res => res.body as AddIndividualCustomerProfileResult),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  addVenueProfile(payload: AddVenueProfileDto): Observable<AddVenueProfileResult> {
    const body = this.mapAddVenueProfilePayload(payload);
    return this.http
      .post(`${AppApiURL}/booking/customers/venues/profile`, body, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map(res => res.body as AddVenueProfileResult),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  getPublicIndividualBookingCustomer(
    bookingCustomerId: string
  ): Observable<IndividualBookingCustomerDetailsVm> {
    const id = encodeURIComponent(bookingCustomerId);
    return this.http.get<IndividualBookingCustomerDetailsVm>(
      `${AppApiURL}/booking/customers/individuals/${id}`,
      { withCredentials: true }
    );
  }

  getVenueBookingCustomerProfile(bookingCustomerId: string): Observable<VenueDetailsVm> {
    const id = encodeURIComponent(bookingCustomerId);
    return this.http.get<VenueDetailsVm>(
      `${AppApiURL}/booking/customers/venues/${id}/profile`,
      { withCredentials: true }
    );
  }

  getPublicVenueBookingCustomer(bookingCustomerId: string): Observable<VenueDetailsVm> {
    const id = encodeURIComponent(bookingCustomerId);
    return this.http.get<VenueDetailsVm>(`${AppApiURL}/booking/customers/venues/${id}`, {
      withCredentials: true
    });
  }

  updateVenueBookingCustomerProfile(
    bookingCustomerId: string,
    payload: UpdateVenueProfileDto
  ): Observable<void> {
    const id = encodeURIComponent(bookingCustomerId);
    const body = this.mapUpdateVenueProfilePayload(payload);
    return this.http
      .put(`${AppApiURL}/booking/customers/venues/${id}/profile`, body, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  updateVenueAddress(bookingCustomerId: string, payload: UpdateVenueAddressDto): Observable<void> {
    const id = encodeURIComponent(bookingCustomerId);
    return this.http
      .put(`${AppApiURL}/booking/customers/venues/${id}/address`, payload, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  updateVenueAvailability(bookingCustomerId: string, isAvailable: boolean): Observable<void> {
    const id = encodeURIComponent(bookingCustomerId);
    const params = new HttpParams().set('isAvailable', String(isAvailable));
    return this.http
      .put(`${AppApiURL}/booking/customers/venues/${id}/availability`, null, {
        params,
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  updateVenuePhotos(bookingCustomerId: string, payload: UpdatePhotosDto): Observable<void> {
    const id = encodeURIComponent(bookingCustomerId);
    return this.http
      .put(`${AppApiURL}/booking/customers/venues/${id}/photos`, payload, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  getVenueApplications(
    bookingCustomerId: string,
    params?: VenueApplicationsQueryParams
  ): Observable<PerformerApplicationVm[]> {
    const id = encodeURIComponent(bookingCustomerId);
    const qs = this.buildApplicationsQueryString(params);
    return this.http
      .get<PerformerApplicationVm[]>(
        `${AppApiURL}/booking/customers/venues/${id}/applications${qs}`,
        { withCredentials: true }
      )
      .pipe(catchError((err: HttpErrorResponse) => throwError(err.error || err)));
  }

  acceptVenueApplication(
    bookingCustomerId: string,
    requestId: string,
    payload?: AcceptApplicationDto
  ): Observable<void> {
    const customerId = encodeURIComponent(bookingCustomerId);
    const appId = encodeURIComponent(requestId);
    return this.http
      .post(`${AppApiURL}/booking/customers/venues/${customerId}/applications/${appId}/accept`, payload ?? {}, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  declineVenueApplication(bookingCustomerId: string, requestId: string): Observable<void> {
    const customerId = encodeURIComponent(bookingCustomerId);
    const appId = encodeURIComponent(requestId);
    return this.http
      .post(
        `${AppApiURL}/booking/customers/venues/${customerId}/applications/${appId}/decline`,
        null,
        { withCredentials: true, observe: 'response' }
      )
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  getVenueTypes(searchText?: string | null): Observable<string[]> {
    let params = new HttpParams();
    const text = (searchText ?? '').trim();
    if (text) {
      params = params.set('searchText', text);
    }
    return this.http
      .get<string[]>(`${AppApiURL}/booking/customers/venues/types`, {
        params,
        withCredentials: true
      })
      .pipe(
        map(list => list ?? []),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  getBookingPerformersList(params?: BookingPerformersListQueryParams): Observable<BookingPerformerListItemVm[]> {
    let httpParams = new HttpParams();
    if (params?.page != null) {
      httpParams = httpParams.set('page', String(params.page));
    }
    if (params?.size != null) {
      httpParams = httpParams.set('size', String(params.size));
    }
    if (params?.searchText) {
      httpParams = httpParams.set('searchText', params.searchText);
    }
    if (params?.ratePerSetMin != null) {
      httpParams = httpParams.set('ratePerSetMin', String(params.ratePerSetMin));
    }
    if (params?.ratePerSetMax != null) {
      httpParams = httpParams.set('ratePerSetMax', String(params.ratePerSetMax));
    }
    if (params?.ratePerHourMin != null) {
      httpParams = httpParams.set('ratePerHourMin', String(params.ratePerHourMin));
    }
    if (params?.ratePerHourMax != null) {
      httpParams = httpParams.set('ratePerHourMax', String(params.ratePerHourMax));
    }
    if (params?.anyRate != null) {
      httpParams = httpParams.set('anyRate', String(params.anyRate));
    }
    if (params?.cityId != null && params.cityId !== 0) {
      httpParams = httpParams.set('cityId', String(params.cityId));
    }
    if (params?.onlyWithVideo != null) {
      httpParams = httpParams.set('onlyWithVideo', String(params.onlyWithVideo));
    }
    if (params?.onlyWithAudio != null) {
      httpParams = httpParams.set('onlyWithAudio', String(params.onlyWithAudio));
    }
    (params?.genres ?? []).forEach(id => {
      httpParams = httpParams.append('genres', String(id));
    });
    (params?.types ?? []).forEach(t => {
      httpParams = httpParams.append('types', PerformerType[t as number] ?? 'Band');
    });
    return this.http
      .get<BookingPerformerListItemVm[]>(`${AppApiURL}/booking/performers/list`, {
        params: httpParams,
        withCredentials: true
      })
      .pipe(
        map(list => list ?? []),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  getVenuesList(params?: VenueListQueryParams): Observable<VenueListItemVm[]> {
    const httpParams = this.buildVenueListParams(params);
    return this.http
      .get<VenueListItemVm[]>(`${AppApiURL}/booking/customers/venues/list`, {
        params: httpParams,
        withCredentials: true
      })
      .pipe(
        map(list => list ?? []),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  getBookingCustomerNotifications(
    bookingCustomerId: string
  ): Observable<BookingCustomerNotificationsSettingsVm> {
    const id = encodeURIComponent(bookingCustomerId);
    return this.http.get<BookingCustomerNotificationsSettingsVm>(
      `${AppApiURL}/booking/customers/${id}/notifications`,
      { withCredentials: true }
    );
  }

  updateBookingCustomerNotifications(
    bookingCustomerId: string,
    payload: UpdateBookingCustomerNotificationSettingsDto
  ): Observable<void> {
    const id = encodeURIComponent(bookingCustomerId);
    return this.http
      .put(`${AppApiURL}/booking/customers/${id}/notifications`, payload, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  registerIndividualCustomer(payload: CreateBookingCustomerDto): Observable<any> {
    const body = this.mapRegisterIndividualPayload(payload);
    return this.http
      .post(`${AppApiURL}/booking/customers/individuals/register`, body, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map(res => res.body),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  registerVenueCustomer(payload: CreateVenueDto): Observable<any> {
    const body = this.mapRegisterVenuePayload(payload);
    return this.http
      .post(`${AppApiURL}/booking/customers/venues/register`, body, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map(res => res.body),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  validateInn(inn: string): Observable<InnInfoResult> {
    const value = String(inn || '').replace(/\D/g, '');
    const params = new HttpParams().set('inn', value);
    return this.http
      .post<unknown>(`${AppApiURL}/booking/customers/validateInn`, null, {
        params,
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map(res => this.mapValidateInnResponse(res.body)),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  private mapValidateInnResponse(body: unknown): InnInfoResult {
    const o = (body ?? {}) as InnInfoResult;
    return {
      inn: o.inn != null ? String(o.inn) : null,
      isApprovedForRegistration: Boolean(o.isApprovedForRegistration),
      emails: o.emails == null ? null : o.emails.map(email => String(email)),
      phoneNumbers: o.phoneNumbers == null ? null : o.phoneNumbers.map(phone => String(phone)),
      name: o.name != null ? String(o.name).trim() || null : null,
      isActive: Boolean(o.isActive)
    };
  }

  sendHiringRequest(bookingCustomerId: string, payload: SendHiringDto): Observable<void> {
    const id = encodeURIComponent(bookingCustomerId);
    return this.http
      .post(`${AppApiURL}/booking/customers/${id}/hirings`, payload, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  getCustomerHirings(
    bookingCustomerId: string,
    params?: {
      page?: number;
      size?: number;
      from?: string | null;
      to?: string | null;
      status?: BookingPerformerHiringStatus | null;
    }
  ): Observable<PerformerHiringVm[]> {
    const id = encodeURIComponent(bookingCustomerId);
    const query: string[] = [];
    if (params?.page != null) {
      query.push(`page=${encodeURIComponent(String(params.page))}`);
    }
    if (params?.size != null) {
      query.push(`size=${encodeURIComponent(String(params.size))}`);
    }
    if (params?.from) {
      query.push(`from=${encodeURIComponent(params.from)}`);
    }
    if (params?.to) {
      query.push(`to=${encodeURIComponent(params.to)}`);
    }
    if (params?.status) {
      query.push(`status=${encodeURIComponent(params.status)}`);
    }
    const qs = query.length ? `?${query.join('&')}` : '';
    return this.http
      .get<PerformerHiringVm[]>(`${AppApiURL}/booking/customers/${id}/hirings${qs}`, {
        withCredentials: true
      })
      .pipe(
        map((list: PerformerHiringVm[]) => (list ?? []).map(normalizePerformerHiringVm)),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  withdrawHiringRequest(bookingCustomerId: string, requestId: string): Observable<void> {
    const customerId = encodeURIComponent(bookingCustomerId);
    const hiringId = encodeURIComponent(requestId);
    return this.http
      .post(`${AppApiURL}/booking/customers/${customerId}/hirings/${hiringId}/withdraw`, null, {
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  markHiringPerformed(
    bookingCustomerId: string,
    requestId: string,
    performedAt?: string | null
  ): Observable<void> {
    const customerId = encodeURIComponent(bookingCustomerId);
    const hiringId = encodeURIComponent(requestId);
    let params = new HttpParams();
    if (performedAt) {
      params = params.set('performedAt', performedAt);
    }
    return this.http
      .post(`${AppApiURL}/booking/customers/${customerId}/hirings/${hiringId}/markPerformed`, null, {
        params,
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  markHiringUnsuccessful(bookingCustomerId: string, requestId: string): Observable<void> {
    const customerId = encodeURIComponent(bookingCustomerId);
    const hiringId = encodeURIComponent(requestId);
    return this.http
      .post(`${AppApiURL}/booking/customers/${customerId}/hirings/${hiringId}/markUnsuccessful`, null, {
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  getPerformerIncomingHirings(
    performerId: string,
    params?: {
      page?: number;
      size?: number;
      from?: string | null;
      to?: string | null;
      status?: BookingPerformerHiringStatus | null;
    }
  ): Observable<PerformerHiringVm[]> {
    const id = encodeURIComponent(performerId);
    const query: string[] = [];
    if (params?.page != null) {
      query.push(`page=${encodeURIComponent(String(params.page))}`);
    }
    if (params?.size != null) {
      query.push(`size=${encodeURIComponent(String(params.size))}`);
    }
    if (params?.from) {
      query.push(`from=${encodeURIComponent(params.from)}`);
    }
    if (params?.to) {
      query.push(`to=${encodeURIComponent(params.to)}`);
    }
    if (params?.status) {
      query.push(`status=${encodeURIComponent(params.status)}`);
    }
    const qs = query.length ? `?${query.join('&')}` : '';
    return this.http
      .get<PerformerHiringVm[]>(`${AppApiURL}/booking/performers/${id}/hirings${qs}`, {
        withCredentials: true
      })
      .pipe(
        map((list: PerformerHiringVm[]) => (list ?? []).map(normalizePerformerHiringVm)),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  acceptPerformerIncomingHiring(performerId: string, requestId: string): Observable<void> {
    const pId = encodeURIComponent(performerId);
    const hId = encodeURIComponent(requestId);
    return this.http
      .post(`${AppApiURL}/booking/performers/${pId}/hirings/${hId}/accept`, null, {
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  declinePerformerIncomingHiring(performerId: string, requestId: string): Observable<void> {
    const pId = encodeURIComponent(performerId);
    const hId = encodeURIComponent(requestId);
    return this.http
      .post(`${AppApiURL}/booking/performers/${pId}/hirings/${hId}/decline`, null, {
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  getPerformerApplications(
    performerId: string,
    params?: PerformerApplicationsQueryParams
  ): Observable<PerformerApplicationVm[]> {
    const id = encodeURIComponent(performerId);
    const qs = this.buildApplicationsQueryString(params);
    return this.http
      .get<PerformerApplicationVm[]>(`${AppApiURL}/booking/performers/${id}/applications${qs}`, {
        withCredentials: true
      })
      .pipe(
        map(list => list ?? []),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  sendPerformerApplication(performerId: string, payload: SendApplicationDto): Observable<void> {
    const id = encodeURIComponent(performerId);
    return this.http
      .post(`${AppApiURL}/booking/performers/${id}/applications`, payload, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  withdrawPerformerApplication(performerId: string, requestId: string): Observable<void> {
    const pId = encodeURIComponent(performerId);
    const rId = encodeURIComponent(requestId);
    return this.http
      .post(`${AppApiURL}/booking/performers/${pId}/applications/${rId}/withdraw`, null, {
        withCredentials: true,
        observe: 'response'
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  getPerformerBookingSettings(performerId: string): Observable<PerformerBookingValuesVm> {
    const id = encodeURIComponent(performerId);
    return this.http
      .get<PerformerBookingValuesVm>(`${AppApiURL}/booking/performers/${id}/settings`, {
        withCredentials: true
      })
      .pipe(
        map(body => this.normalizePerformerBookingValues(body)),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  updatePerformerBookingAvailability(
    performerId: string,
    payload: UpdatePerformerAvailabilityDto
  ): Observable<void> {
    const id = encodeURIComponent(performerId);
    return this.http
      .put(`${AppApiURL}/booking/performers/${id}/settings/availability`, payload, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  updatePerformerBookingRate(performerId: string, payload: PerformerRateDto): Observable<void> {
    const id = encodeURIComponent(performerId);
    return this.http
      .put(`${AppApiURL}/booking/performers/${id}/settings/rate`, payload, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  getPerformerBookingNotifications(
    performerId: string
  ): Observable<BookingPerformerNotificationsSettingsVm> {
    const id = encodeURIComponent(performerId);
    return this.http
      .get<BookingPerformerNotificationsSettingsVm>(
        `${AppApiURL}/booking/performers/${id}/settings/notifications`,
        { withCredentials: true }
      )
      .pipe(
        map(body => this.normalizePerformerNotificationsSettings(body)),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  updatePerformerBookingNotifications(
    performerId: string,
    payload: UpdateBookingPerformerNotificationsSettingsDto
  ): Observable<void> {
    const id = encodeURIComponent(performerId);
    return this.http
      .put(`${AppApiURL}/booking/performers/${id}/settings/notifications`, payload, {
        withCredentials: true,
        observe: 'response',
        headers: { 'Content-Type': 'application/json' }
      })
      .pipe(
        map(() => undefined),
        catchError((err: HttpErrorResponse) => throwError(err.error || err))
      );
  }

  private normalizePerformerNotificationsSettings(
    body: BookingPerformerNotificationsSettingsVm | null | undefined
  ): BookingPerformerNotificationsSettingsVm {
    const o = body ?? {};
    const venuesOnly = !!(
      o.venuesOnlyWithAppliedBookingNotificationsEnabled ?? o.venuesOnlyWithAppliedBooking
    );
    return {
      mainBookingNotificationsEnabled: !!o.mainBookingNotificationsEnabled,
      newVenueRegistrationNotificationsEnabled: !!o.newVenueRegistrationNotificationsEnabled,
      venuesOnlyWithAppliedBooking: venuesOnly,
      venuesOnlyWithAppliedBookingNotificationsEnabled: venuesOnly
    };
  }

  private normalizePerformerBookingValues(body: PerformerBookingValuesVm | null | undefined): PerformerBookingValuesVm {
    const o = body ?? {};
    const availability =
      (o.availability != null ? String(o.availability) : null) ||
      (o.avialability != null ? String(o.avialability) : null) ||
      null;
    return {
      availability,
      avialability: availability,
      isAvailable: !!o.isAvailable,
      rates: o.rates ?? null
    };
  }

  private mapUpdateProfilePayload(payload: UpdateIndividualCustomerProfileDto): Record<string, unknown> {
    return {
      name: payload.name ?? null,
      cityId: payload.cityId,
      socialLinks: this.mapSocialLinksForApi(payload.socialLinks)
    };
  }

  private mapAddIndividualProfilePayload(payload: AddIndividualCustomerProfileDto): Record<string, unknown> {
    const out: Record<string, unknown> = {
      cityId: payload.cityId,
      socialLinks: this.mapSocialLinksForApi(payload.socialLinks ?? null)
    };
    if (payload.name != null && String(payload.name).trim() !== '') {
      out.name = String(payload.name).trim();
    } else {
      out.name = null;
    }
    return out;
  }

  private mapRegisterIndividualPayload(payload: CreateBookingCustomerDto): Record<string, unknown> {
    const rep = payload.representer || ({} as any);
    const phone = rep.phoneNumber || ({} as any);
    const representer: Record<string, unknown> = {
      email: rep.email ?? '',
      name: rep.name ?? '',
      lastName: rep.lastName ?? '',
      phoneNumber: {
        number: phone.number ?? '',
        hasTelegram: !!phone.hasTelegram,
        hasMax: !!phone.hasMax
      }
    };
    if (rep.cityId != null && Number(rep.cityId) > 0) {
      representer.cityId = Number(rep.cityId);
    }
    if (rep.birthDate) {
      representer.birthDate = rep.birthDate;
    }
    const out: Record<string, unknown> = {
      representer,
      picKey: payload.picKey ?? null,
      socialLinks: this.mapSocialLinksForApi(payload.socialLinks ?? null)
    };
    const pwd = payload.password != null ? String(payload.password) : '';
    if (pwd) {
      out.password = pwd;
    }
    return out;
  }

  private mapRegisterVenuePayload(payload: CreateVenueDto): Record<string, unknown> {
    const base = this.mapRegisterIndividualPayload(payload);
    const contacts = payload.contacts ?? payload.representer?.phoneNumber ?? null;
    const out: Record<string, unknown> = {
      ...base,
      venueName: payload.venueName ?? null,
      inn: payload.inn ?? null,
      format: payload.format ?? null,
      description: payload.description ?? null,
      venueTypeTag: payload.venueTypeTag ?? null,
      preferredGenres: payload.preferredGenres ?? null,
      preferredPerformerTypes: payload.preferredPerformerTypes ?? null,
      readyToAcceptApplications: payload.readyToAcceptApplications ?? false,
      contacts: this.mapPhoneNumberDto(contacts)
    };
    if (payload.address) {
      out.address = payload.address;
    }
    if (payload.facilities != null) {
      out.facilities = payload.facilities;
    }
    return out;
  }

  private mapAddVenueProfilePayload(payload: AddVenueProfileDto): Record<string, unknown> {
    return {
      contacts: this.mapPhoneNumberDto(payload.contacts ?? payload.phoneNumber),
      socialLinks: this.mapSocialLinksForApi(payload.socialLinks ?? null),
      venueName: payload.venueName ?? null,
      inn: payload.inn ?? null,
      format: payload.format ?? null,
      description: payload.description ?? null,
      venueTypeTag: payload.venueTypeTag ?? null,
      preferredGenres: payload.preferredGenres ?? null,
      preferredPerformerTypes: payload.preferredPerformerTypes ?? null,
      readyToAcceptApplications: payload.readyToAcceptApplications ?? false,
      address: payload.address ?? null,
      facilities: payload.facilities ?? null
    };
  }

  private mapUpdateVenueProfilePayload(payload: UpdateVenueProfileDto): Record<string, unknown> {
    return {
      name: payload.name ?? null,
      description: payload.description ?? null,
      format: payload.format ?? null,
      venueTypeTag: payload.venueTypeTag ?? null,
      city: payload.city,
      preferredGenres: payload.preferredGenres ?? null,
      preferredPerformerTypes: payload.preferredPerformerTypes ?? null,
      socialLinks: this.mapSocialLinksForApi(payload.socialLinks ?? null),
      facilities: payload.facilities ?? null,
      contacts: this.mapPhoneNumberDto(payload.contacts)
    };
  }

  private mapPhoneNumberDto(phone?: PhoneNumberDto | null): Record<string, unknown> | null {
    if (!phone) {
      return null;
    }
    return {
      number: phone.number ?? '',
      hasTelegram: !!phone.hasTelegram,
      hasMax: !!phone.hasMax
    };
  }

  private parsePhotoBufferKey(body: string | null): string {
    const raw = (body ?? '').trim();
    if (!raw) {
      return '';
    }
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'string' ? parsed : String(parsed);
    } catch {
      return raw.replace(/^"|"$/g, '');
    }
  }

  private buildApplicationsQueryString(params?: VenueApplicationsQueryParams): string {
    const query: string[] = [];
    if (params?.page != null) {
      query.push(`page=${encodeURIComponent(String(params.page))}`);
    }
    if (params?.size != null) {
      query.push(`size=${encodeURIComponent(String(params.size))}`);
    }
    if (params?.from) {
      query.push(`from=${encodeURIComponent(params.from)}`);
    }
    if (params?.to) {
      query.push(`to=${encodeURIComponent(params.to)}`);
    }
    if (params?.status) {
      query.push(`status=${encodeURIComponent(params.status)}`);
    }
    return query.length ? `?${query.join('&')}` : '';
  }

  private buildVenueListParams(params?: VenueListQueryParams): HttpParams {
    let httpParams = new HttpParams();
    if (params?.page != null) {
      httpParams = httpParams.set('page', String(params.page));
    }
    if (params?.size != null) {
      httpParams = httpParams.set('size', String(params.size));
    }
    if (params?.searchText) {
      httpParams = httpParams.set('searchText', params.searchText);
    }
    if (params?.venueTypeTag) {
      httpParams = httpParams.set('venueTypeTag', params.venueTypeTag);
    }
    if (params?.cityId != null) {
      httpParams = httpParams.set('cityId', String(params.cityId));
    }
    if (params?.readyToAcceptApplications != null) {
      httpParams = httpParams.set('readyToAcceptApplications', String(params.readyToAcceptApplications));
    }
    (params?.preferredGenres ?? []).forEach(id => {
      httpParams = httpParams.append('preferredGenres', String(id));
    });
    (params?.preferredPerformerTypes ?? []).forEach(type => {
      httpParams = httpParams.append('preferredPerformerTypes', String(type));
    });
    return httpParams;
  }

  private mapSocialLinksForApi(socialLinks: SocialLinkDto[] | null | undefined): unknown[] | null {
    const list = (socialLinks ?? [])
      .map(link => ({
        platform: SocialPlatform[link.platform as number] ?? 'Instagram',
        url: (link.url ?? '').trim()
      }))
      .filter(l => !!l.url);
    return list.length ? list : null;
  }
}
